import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../../i18n/useP';
import Card from '../../../components/Card';
import { MOIS, MOIS_COURT } from '../engine/constants';
import { formatSwissAmount } from '../billing/formatSwiss';
import './PersonnelManualForm.css';

// Charges personnel — mode « Manuel » (simple). Saisie directe, sans lien People :
//  · salaire brut mensuel (base) + 13e/14e (cochables, mois choisi)
//  · lignes d'ajustement libres (embauche, départ, prime… ±, mois, libellé)
//  · recalcul brut mensuel / 13-14e / total brut 12 mois / salaire net
//  · taux charges sociales employé (défaut Neuchâtel AVS/AI/APG + AC, modifiable)
//  · autres charges sociales part employeur (accident / maladie-PDG / LPP) : taux
//    + échéancier de paiement en face de chaque ligne.
// Persisté par scénario (sessionStorage). Câblage moteur cash traité à part.

// Échéancier stocké par MOIS CALENDAIRE (0=janv … 11=déc), indépendant du mois de
// départ du plan — un défaut « paiement en mars » reste mars quel que soit le départ.
const mset = (...ms) => { const a = Array(12).fill(false); ms.forEach((m) => { a[m] = true; }); return a; };

// Jeu de taux + échéanciers d'exemple (Neuchâtel), chargeable via bouton.
const EXAMPLE = {
  employeeRate: 11,                                        // charges sociales part salarié
  ins: {
    base:     { rate: 9,   months: Array(12).fill(true) }, // cotisations de base (AVS/AI/APG/AC empl.) — mensuel
    accident: { rate: 1,   months: mset(0) },              // AAP, AANP — janvier
    laa:      { rate: 0.1, months: mset(0, 3, 6, 9) },     // LAA-LAAC — trimestriel en avance (jan/avr/juil/oct)
    pgm:      { rate: 0.6, months: mset(0, 3, 6, 9) },     // PGM — trimestriel en avance
    lpp:      { rate: 0,   months: mset(2, 5, 8, 11) },    // LPP — trimestriel (mars/juin/sep/déc)
  },
};
const DEF = {
  grossMonthly: 0,
  thirteenth: { on: false, month: 6 },
  fourteenth: { on: false, month: 11 },
  adjustments: [],                       // { label, month (index fenêtre), amount }
  employeeRate: EXAMPLE.employeeRate,
  ins: {
    base:     { rate: EXAMPLE.ins.base.rate,     months: [...EXAMPLE.ins.base.months] },
    accident: { rate: EXAMPLE.ins.accident.rate, months: [...EXAMPLE.ins.accident.months] },
    laa:      { rate: EXAMPLE.ins.laa.rate,      months: [...EXAMPLE.ins.laa.months] },
    pgm:      { rate: EXAMPLE.ins.pgm.rate,      months: [...EXAMPLE.ins.pgm.months] },
    lpp:      { rate: EXAMPLE.ins.lpp.rate,      months: [...EXAMPLE.ins.lpp.months] },
  },
};

const num = (v) => Number(v) || 0;
const parse = (s) => {
  const raw = String(s).replace(/[^0-9.,-]/g, '').replace(',', '.');
  return raw === '' ? null : Number(raw);
};

function RateInput({ value, onChange, disabled, suffix = '%' }) {
  return (
    <span className="pmf-num">
      <input type="text" inputMode="decimal" disabled={disabled}
        value={value ?? ''} onChange={(e) => onChange(parse(e.target.value))} />
      <span className="pmf-num__suffix">{suffix}</span>
    </span>
  );
}

export const PERSONNEL_DEF = DEF;

// Applique les défauts sur un formulaire partiel/absent.
function normalize(form) {
  return {
    ...DEF, ...form,
    thirteenth: { ...DEF.thirteenth, ...(form?.thirteenth || {}) },
    fourteenth: { ...DEF.fourteenth, ...(form?.fourteenth || {}) },
    adjustments: form?.adjustments || [],
    ins: { ...DEF.ins, ...(form?.ins || {}) },
  };
}

// Décaissement d'une cotisation (fenêtre-indexé) : provision rate%×brut chaque
// mois, décaissée au mois calendaire d'échéance (cumul depuis la précédente).
function disburseIns(rate, months, monthlyGross, win) {
  const out = Array(12).fill(0);
  let acc = 0;
  for (let i = 0; i < 12; i += 1) {
    acc += (num(rate) / 100) * monthlyGross[i];
    if (months[win[i].m]) { out[i] = acc; acc = 0; }
  }
  return out;
}

// Séries de charges personnel (fenêtre-indexées) alimentant le moteur cash quand
// le mode Manuel est actif. Même dérivation que l'affichage du formulaire.
export function computeManualPersonnel(form, startMonth) {
  const f = normalize(form);
  const start = startMonth || 0;
  const win = Array.from({ length: 12 }, (_, i) => ({ m: (start + i) % 12 }));
  const base = num(f.grossMonthly);
  const monthlyGross = win.map((_, i) =>
    base
    + (f.thirteenth.on && num(f.thirteenth.month) === i ? base : 0)
    + (f.fourteenth.on && num(f.fourteenth.month) === i ? base : 0)
    + f.adjustments.reduce((s, a) => s + (num(a.month) === i ? num(a.amount) : 0), 0));
  const monthlyNet = monthlyGross.map((v) => v * (1 - num(f.employeeRate) / 100));
  const baseDisb = disburseIns(f.ins.base.rate, f.ins.base.months, monthlyGross, win);
  const assurDisb = win.map((_, i) =>
    ['accident', 'laa', 'pgm', 'lpp'].reduce((s, k) =>
      s + disburseIns(f.ins[k].rate, f.ins[k].months, monthlyGross, win)[i], 0));
  return {
    personnelProjection: { net: monthlyNet, gross: monthlyGross },
    chargesSocialesMonthly: baseDisb,
    assurancesMonthly: assurDisb,
  };
}

export default function PersonnelManualForm({ scenario, locked, form, onChange }) {
  const p = useP();
  const { t } = useTranslation();
  const setForm = onChange;
  const f = normalize(form);

  // Fenêtre : 12 mois à venir à partir du mois de départ du scénario.
  const start = scenario?.start_month ?? 0;
  const startYear = scenario?.start_year ?? new Date().getFullYear();
  const win = Array.from({ length: 12 }, (_, i) => {
    const abs = start + i;
    return { i, m: abs % 12, year: startYear + Math.floor(abs / 12) };
  });
  const monthLabel = (i) => `${MOIS_COURT[win[i].m]} ${win[i].year}`;

  const patch = (p) => setForm({ ...f, ...p });
  const setIns = (kind, p) => patch({ ins: { ...f.ins, [kind]: { ...f.ins[kind], ...p } } });
  // cal = mois calendaire (0..11).
  const toggleInsMonth = (kind, cal) => {
    const ms = [...f.ins[kind].months]; ms[cal] = !ms[cal]; setIns(kind, { months: ms });
  };
  const loadExample = () => patch({
    employeeRate: EXAMPLE.employeeRate,
    ins: {
      base:     { rate: EXAMPLE.ins.base.rate,     months: [...EXAMPLE.ins.base.months] },
      accident: { rate: EXAMPLE.ins.accident.rate, months: [...EXAMPLE.ins.accident.months] },
      laa:      { rate: EXAMPLE.ins.laa.rate,      months: [...EXAMPLE.ins.laa.months] },
      pgm:      { rate: EXAMPLE.ins.pgm.rate,      months: [...EXAMPLE.ins.pgm.months] },
      lpp:      { rate: EXAMPLE.ins.lpp.rate,      months: [...EXAMPLE.ins.lpp.months] },
    },
  });
  const addAdj = () => patch({ adjustments: [...f.adjustments, { label: '', month: 0, amount: 0 }] });
  const setAdj = (idx, p) => patch({ adjustments: f.adjustments.map((a, k) => (k === idx ? { ...a, ...p } : a)) });
  const delAdj = (idx) => patch({ adjustments: f.adjustments.filter((_, k) => k !== idx) });

  // ── Recalcul ──────────────────────────────────────────────────────────────
  const base = num(f.grossMonthly);
  const baseRow = win.map(() => base);                                   // brut de base par mois
  const extra = win.map((_, i) =>
    (f.thirteenth.on && num(f.thirteenth.month) === i ? base : 0)
    + (f.fourteenth.on && num(f.fourteenth.month) === i ? base : 0));    // 13e / 14e
  const hasExtra = f.thirteenth.on || f.fourteenth.on;
  const adjMonthly = (a) => win.map((_, i) => (num(a.month) === i ? num(a.amount) : 0));
  const monthlyGross = win.map((_, i) =>
    base + extra[i] + f.adjustments.reduce((s, a) => s + (num(a.month) === i ? num(a.amount) : 0), 0));
  const rate = num(f.employeeRate) / 100;
  const monthlyNet = monthlyGross.map((v) => v * (1 - rate));
  const totalGross = monthlyGross.reduce((a, b) => a + b, 0);
  const totalNet = monthlyNet.reduce((a, b) => a + b, 0);
  const sum = (arr) => arr.reduce((a, b) => a + num(b), 0);
  const cells = (arr) => win.map((_, i) => <td key={i}>{num(arr[i]) ? formatSwissAmount(num(arr[i])) : '–'}</td>);

  const INS = [
    { key: 'base',     label: p('Cotisations de base'), short: p('Cotisations de base') },
    { key: 'accident', label: 'AAP, AANP', short: 'AAP, AANP' },
    { key: 'laa',      label: 'LAA-LAAC', short: 'LAA-LAAC' },
    { key: 'pgm',      label: 'PGM', short: 'PGM' },
    { key: 'lpp',      label: 'LPP', short: 'LPP' },
  ];

  // Décaissements par cotisation : chaque cotisation provisionne rate% × brut
  // chaque mois ; à chaque mois d'échéance (calendaire) coché, on décaisse le
  // cumul provisionné depuis l'échéance précédente.
  const disburse = (r, months) => {
    const out = Array(12).fill(0);
    let acc = 0;
    for (let i = 0; i < 12; i++) {
      acc += (num(r) / 100) * monthlyGross[i];
      if (months[win[i].m]) { out[i] = acc; acc = 0; }
    }
    return out;
  };
  const insDisb = INS.map((row) => ({ ...row, monthly: disburse(f.ins[row.key].rate, f.ins[row.key].months || []) }));

  // ── Modale taux & échéancier ────────────────────────────────────────────────
  const [csOpen, setCsOpen] = useState(false);

  return (
    <Card title={p('Charges personnel — saisie manuelle')}>
      <div className="pmf">
        {/* Base brute + 13e/14e + popover taux & échéancier */}
        <div className="pmf-grid">
          <label className="pmf-field">
            <span className="pmf-field__lab">{p('Salaire brut mensuel')}</span>
            <span className="pmf-num">
              <input type="text" inputMode="decimal" disabled={locked}
                value={f.grossMonthly ?? ''} onChange={(e) => patch({ grossMonthly: parse(e.target.value) || 0 })} />
              <span className="pmf-num__suffix">CHF</span>
            </span>
          </label>

          <div className="pmf-field">
            <label className="pmf-check">
              <input type="checkbox" disabled={locked} checked={!!f.thirteenth.on}
                onChange={(e) => patch({ thirteenth: { ...f.thirteenth, on: e.target.checked } })} />
              <span>13e salaire</span>
            </label>
            <select disabled={locked || !f.thirteenth.on} value={f.thirteenth.month}
              onChange={(e) => patch({ thirteenth: { ...f.thirteenth, month: Number(e.target.value) } })}>
              {win.map((_, i) => <option key={i} value={i}>{monthLabel(i)}</option>)}
            </select>
          </div>

          <div className="pmf-field">
            <label className="pmf-check">
              <input type="checkbox" disabled={locked} checked={!!f.fourteenth.on}
                onChange={(e) => patch({ fourteenth: { ...f.fourteenth, on: e.target.checked } })} />
              <span>14e salaire</span>
            </label>
            <select disabled={locked || !f.fourteenth.on} value={f.fourteenth.month}
              onChange={(e) => patch({ fourteenth: { ...f.fourteenth, month: Number(e.target.value) } })}>
              {win.map((_, i) => <option key={i} value={i}>{monthLabel(i)}</option>)}
            </select>
          </div>

          {/* Bouton (style primary, comme Finance) → modale centrée */}
          <div className="pmf-field">
            <span className="pmf-field__lab">{p('Charges sociales')}</span>
            <button type="button" className="pmf-cs__btn" onClick={() => setCsOpen(true)}>
              {p('Taux & échéancier')}
            </button>
          </div>
        </div>

        {/* Modale centrée : taux (employé + employeur) et échéancier */}
        {csOpen && (
          <>
            <div className="pmf-cs__scrim" onClick={() => setCsOpen(false)} />
            <div className="pmf-cs__modal" role="dialog" aria-label={t('phrases:p_9da13c9373')}>
              <div className="pmf-cs__head">
                <span className="pmf-cs__title">{p('Taux & échéancier des charges sociales')}</span>
                <div className="pmf-cs__head-actions">
                  {!locked && <button type="button" className="pmf-cs__example" onClick={loadExample}>{p("Charger l'exemple")}</button>}
                  <button type="button" className="pmf-cs__x" onClick={() => setCsOpen(false)} aria-label={p('Fermer')}>×</button>
                </div>
              </div>
              <div className="pmf-cs__body">
                <label className="pmf-cs__rate">
                  <span>{p('Taux charges sociales employé')}</span>
                  <RateInput value={f.employeeRate} disabled={locked}
                    onChange={(v) => patch({ employeeRate: v ?? 0 })} />
                </label>
                <div className="pmf-cs__sub">{p('Cotisations — part employeur')}</div>
                {INS.map((row) => (
                  <div key={row.key} className="pmf-cs__row">
                    <span className="pmf-cs__label">{row.label}</span>
                    <RateInput value={f.ins[row.key].rate} disabled={locked}
                      onChange={(v) => setIns(row.key, { rate: v ?? 0 })} />
                    <div className="pmf-chips" title={t('phrases:p_ea99c4c8f8')}>
                      {MOIS.map((lab, m) => (
                        <button key={m} type="button" disabled={locked}
                          className={`pmf-chip${f.ins[row.key].months[m] ? ' is-on' : ''}`}
                          onClick={() => toggleInsMonth(row.key, m)}>{lab}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Ajustements de salaires (± , mois, libellé libre) */}
        <div className="pmf-block">
          <span className="pmf-block__lab">{p('Ajustements de salaires (embauche / départ / prime…)')}</span>
          {f.adjustments.length > 0 && (
            <div className="pmf-adj">
              {f.adjustments.map((a, idx) => (
                <div key={idx} className="pmf-adj__row">
                  <input className="pmf-adj__label" type="text" placeholder={t('phrases:p_4b75a802c7')}
                    disabled={locked} value={a.label}
                    onChange={(e) => setAdj(idx, { label: e.target.value })} />
                  <select disabled={locked} value={a.month}
                    onChange={(e) => setAdj(idx, { month: Number(e.target.value) })}>
                    {win.map((_, i) => <option key={i} value={i}>{monthLabel(i)}</option>)}
                  </select>
                  <span className="pmf-num">
                    <input type="text" inputMode="decimal" disabled={locked} placeholder="± CHF"
                      value={a.amount ?? ''} onChange={(e) => setAdj(idx, { amount: parse(e.target.value) || 0 })} />
                    <span className="pmf-num__suffix">CHF</span>
                  </span>
                  <button type="button" className="pmf-adj__del" disabled={locked}
                    title={p("Supprimer l'ajustement")} onClick={() => delAdj(idx)}>×</button>
                </div>
              ))}
            </div>
          )}
          {!locked && <button type="button" className="pmf-add" onClick={addAdj}>{p('+ Ajustement salaires')}</button>}
        </div>

        {/* Recalcul : brut mensuel · 13/14e · total brut · net */}
        <div className="pmf-block">
          <span className="pmf-block__lab">{p('Salaires recalculés — 12 mois à venir')}</span>
          <div className="pmf-recap-wrap">
            <table className="pmf-recap">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>{p('Composante')}</th>
                  {win.map((_, i) => <th key={i}>{MOIS_COURT[win[i].m]}</th>)}
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ textAlign: 'left' }}>{p('Salaires bruts')}</td>
                  {cells(baseRow)}
                  <td>{formatSwissAmount(sum(baseRow))}</td>
                </tr>
                {f.adjustments.map((a, idx) => (
                  <tr key={idx}>
                    <td style={{ textAlign: 'left' }}>{a.label?.trim() || p('Ajustement')}</td>
                    {cells(adjMonthly(a))}
                    <td>{formatSwissAmount(num(a.amount))}</td>
                  </tr>
                ))}
                {hasExtra && (
                  <tr>
                    <td style={{ textAlign: 'left' }}>{p('13e / 14e')}</td>
                    {cells(extra)}
                    <td>{formatSwissAmount(sum(extra))}</td>
                  </tr>
                )}
                <tr className="pmf-recap__total">
                  <td style={{ textAlign: 'left' }}>{p('Total bruts')}</td>
                  {cells(monthlyGross)}
                  <td>{formatSwissAmount(totalGross)}</td>
                </tr>
                <tr className="pmf-recap__net">
                  <td style={{ textAlign: 'left' }}>{p('Salaires nets')}</td>
                  {cells(monthlyNet)}
                  <td>{formatSwissAmount(totalNet)}</td>
                </tr>
                {insDisb.map((row) => (
                  <tr key={row.key} className="pmf-recap__disb">
                    <td style={{ textAlign: 'left' }}>{row.short}</td>
                    {cells(row.monthly)}
                    <td>{formatSwissAmount(sum(row.monthly))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Card>
  );
}
